import type { FormEvent } from 'react';
import { useMemo, useState } from 'react';
import type {
  CustomerManagerPortfolioCustomer,
  CustomerManagerRiskSignal,
  OpsUserRecord,
} from '@nadoutong/contracts';
import { RefreshCw, UserCheck, UserPlus } from 'lucide-react';
import { OpsApiError } from '../api/client.ts';
import { useAssignCustomerManager } from '../api/customerOwnership.ts';
import type { CustomerPortfolioSortKey } from '../api/customerManagerWorkspace.ts';
import {
  useClaimCustomerFromPool,
  useCustomerManagerCustomerPool,
} from '../api/customerManagerWorkspace.ts';
import { useOpsUsers } from '../api/opsUsers.ts';
import { usePricing } from '../api/pricing.ts';
import {
  ROLE_OPS_ADMIN,
  ROLE_OPS_CUSTOMER_MANAGER,
  hasAnyOpsRole,
} from '../auth/opsRoles.ts';
import { BrandChip } from '../components/BrandChip.tsx';
import { BrandScopeBanner } from '../components/BrandScopeBanner.tsx';
import { CustomerSummaryCard } from '../components/CustomerSummaryCard.tsx';
import type { CustomerSummaryFact } from '../components/CustomerSummaryCard.tsx';
import { CustomerCell } from '../components/identity/CustomerCell.tsx';
import {
  formatLocalTimestamp,
  formatUsdMicrosOrCentsAsCnyDisplayBalance,
} from '../components/formatters.ts';
import { PortfolioSortControl } from '../components/PortfolioSortControl.tsx';
import type { PortfolioSortColumn } from '../components/PortfolioSortControl.tsx';
import { StatusChip } from '../components/StatusChip.tsx';
import { useSession } from '../hooks/useSession.tsx';
import { useIsMobile } from '../hooks/useViewport.ts';
import { useT } from '../i18n/index.ts';
import type { PortfolioSort } from './customerPortfolioSort.ts';
import { nextPortfolioSort } from './customerPortfolioSort.ts';
import {
  Button,
  EmptyState,
  Input,
  KpiCard,
  PageHeader,
  Select,
  SortableTh,
  TBody,
  THead,
  Table,
  Td,
  Th,
  Tr,
} from '../ui/index.ts';

const PAGE_LIMIT = 50;

/**
 * Newest first (#1938). The pool is a queue of customers nobody has picked up
 * yet, so the ones that just arrived are the ones a manager wants to see.
 */
const DEFAULT_SORT: PortfolioSort = { key: 'created_at', dir: 'desc' };

interface Scope {
  readonly email: string;
  readonly offset: number;
  /**
   * Optional brand narrowing filter ('' = all brands). On the pool the
   * filter is honoured for admin, finance and self-scoped customer
   * managers (ops-api forwards it; it can only narrow); it is ignored for
   * sales leads, whose pool stays pinned to their profile brand (#1540).
   * On the workbench it remains admin-only.
   */
  readonly brand: string;
  readonly sort: PortfolioSort;
}

interface ClaimStatus {
  readonly kind: 'success' | 'error';
  readonly message: string;
}

function fmtInt(value: number): string {
  return value.toLocaleString('en-US');
}

function truncate(value: string, head = 8, tail = 4): string {
  if (value.length <= head + tail + 3) return value;
  return `${value.slice(0, head)}...${value.slice(-tail)}`;
}

function riskVariant(signal: CustomerManagerRiskSignal): 'error' | 'warning' | 'neutral' {
  if (signal.severity === 'critical') return 'error';
  if (signal.severity === 'warning') return 'warning';
  return 'neutral';
}

function opsUserLabel(user: OpsUserRecord): string {
  const displayName = user.display_name.trim();
  if (displayName.length > 0 && displayName !== user.email) {
    return `${displayName} · ${user.email}`;
  }
  return user.email;
}

function hasCustomerManagerRole(user: OpsUserRecord): boolean {
  return user.roles.includes(ROLE_OPS_CUSTOMER_MANAGER);
}

export function CustomerManagerPoolPage(): JSX.Element {
  const t = useT();
  const isMobile = useIsMobile();
  const { user } = useSession();
  const roles = user?.roles ?? [];
  const isAdmin = hasAnyOpsRole(roles, [ROLE_OPS_ADMIN]);
  const canClaim = !isAdmin && hasAnyOpsRole(roles, [ROLE_OPS_CUSTOMER_MANAGER]);
  const [draftEmail, setDraftEmail] = useState('');
  const [scope, setScope] = useState<Scope>({
    email: '',
    offset: 0,
    brand: '',
    sort: DEFAULT_SORT,
  });
  const [status, setStatus] = useState<ClaimStatus | null>(null);
  const query = useCustomerManagerCustomerPool({
    limit: PAGE_LIMIT,
    offset: scope.offset,
    email: scope.email,
    brand: scope.brand.length > 0 ? scope.brand : undefined,
    sort: scope.sort.key,
    dir: scope.sort.dir,
  });
  const pricing = usePricing();
  const cnyPerUsd = pricing.data?.policy?.cny_per_usd ?? null;
  const claim = useClaimCustomerFromPool();
  const assign = useAssignCustomerManager();
  const opsUsers = useOpsUsers({ enabled: isAdmin });
  const assignableManagers = useMemo(
    () =>
      (opsUsers.data?.users ?? [])
        .filter((manager) => manager.disabled_at === null && hasCustomerManagerRole(manager))
        .sort((a, b) => opsUserLabel(a).localeCompare(opsUserLabel(b))),
    [opsUsers.data],
  );

  const rows = useMemo(() => query.data?.customers ?? [], [query.data]);
  const pool = query.data?.pool;
  const total = query.data?.total ?? 0;
  const currentOffset = query.data?.offset ?? scope.offset;
  const hasPrev = currentOffset > 0;
  const hasNext = currentOffset + rows.length < total;

  function handleSearch(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setScope((prev) => ({ ...prev, email: draftEmail.trim(), offset: 0 }));
  }

  function handleClear(): void {
    setDraftEmail('');
    setScope((prev) => ({ ...prev, email: '', offset: 0 }));
  }

  // Re-sorting reshuffles the whole book server-side, so the operator's
  // current offset points at an unrelated slice — go back to page one.
  function handleSort(key: CustomerPortfolioSortKey): void {
    setScope((prev) => ({ ...prev, sort: nextPortfolioSort(prev.sort, key), offset: 0 }));
  }

  function handleSortChange(sort: PortfolioSort | null): void {
    if (sort === null) return;
    setScope((prev) => ({ ...prev, sort, offset: 0 }));
  }

  const poolSortColumns: readonly PortfolioSortColumn[] = [
    { key: 'email', label: t('customer_manager_pool.column.customer') },
    { key: 'org', label: t('customer_manager_pool.column.org') },
    { key: 'created_at', label: t('customer_manager_pool.column.created_at') },
    { key: 'balance', label: t('customer_manager_pool.column.balance') },
    { key: 'requests', label: t('customer_manager_pool.column.usage') },
    { key: 'keys', label: t('customer_manager_pool.column.keys') },
    { key: 'risks', label: t('customer_manager_pool.column.risks') },
  ];

  async function handleClaim(customer: CustomerManagerPortfolioCustomer): Promise<void> {
    setStatus(null);
    try {
      await claim.mutateAsync(customer.org_id);
      setStatus({
        kind: 'success',
        message: t('customer_manager_pool.claim.claimed', { email: customer.email }),
      });
    } catch (err) {
      if (err instanceof OpsApiError && err.code === 'CUSTOMER_ALREADY_CLAIMED') {
        setStatus({
          kind: 'error',
          message: t('customer_manager_pool.claim.conflict'),
        });
        void query.refetch();
        return;
      }
      setStatus({
        kind: 'error',
        message:
          err instanceof OpsApiError
            ? `${err.code}: ${err.message}`
            : t('common.network_error'),
      });
    }
  }

  async function handleAssign(
    customer: CustomerManagerPortfolioCustomer,
    customerManagerOpsUserId: string,
  ): Promise<void> {
    setStatus(null);
    const manager = assignableManagers.find(
      (candidate) => candidate.id === customerManagerOpsUserId,
    );
    try {
      await assign.mutateAsync({
        orgId: customer.org_id,
        customerManagerOpsUserId,
      });
      setStatus({
        kind: 'success',
        message: t('customer_manager_pool.assign.assigned', {
          email: customer.email,
          manager: manager === undefined ? customerManagerOpsUserId : opsUserLabel(manager),
        }),
      });
      void query.refetch();
    } catch (err) {
      setStatus({
        kind: 'error',
        message:
          err instanceof OpsApiError
            ? `${err.code}: ${err.message}`
            : t('common.network_error'),
      });
    }
  }

  return (
    <section data-testid="customer-manager-pool-page" className="flex flex-col gap-6">
      <PageHeader
        title={t('customer_manager_pool.title')}
        sub={
          isAdmin
            ? t('customer_manager_pool.admin_subtitle')
            : t('customer_manager_pool.subtitle')
        }
        actions={
          <>
            <span className="font-mono text-micro text-paper-low">
              {query.data?.generated_at !== undefined
                ? t('customer_manager_pool.generated_at', {
                    time: formatLocalTimestamp(query.data.generated_at),
                  })
                : t('customer_manager_pool.generated_pending')}
            </span>
            <Button
              onClick={() => void query.refetch()}
              data-testid="customer-manager-pool-refresh"
            >
              <RefreshCw aria-hidden="true" className="h-3.5 w-3.5" />
              {t('common.refresh')}
            </Button>
          </>
        }
      />

      <BrandScopeBanner
        testIdPrefix="customer-manager-pool"
        poolView
        brandFilter={scope.brand}
        onBrandFilterChange={(brand) =>
          setScope((prev) => ({ ...prev, brand, offset: 0 }))
        }
      />

      <div className="grid grid-cols-2 gap-3 max-md:grid-flow-dense xl:grid-cols-5">
        <KpiCard
          label={t('customer_manager_pool.metric.available')}
          value={fmtInt(pool?.available_customers ?? 0)}
          testId="customer-manager-pool-metric-available"
        />
        <KpiCard
          label={t('customer_manager_pool.metric.balance')}
          value={formatUsdMicrosOrCentsAsCnyDisplayBalance(
            pool?.total_balance_usd_micros,
            pool?.total_balance_usd_cents ?? 0,
            cnyPerUsd,
          )}
          className="max-md:col-span-2"
          testId="customer-manager-pool-metric-balance"
        />
        <KpiCard
          label={t('customer_manager_pool.metric.active_keys')}
          value={fmtInt(pool?.active_api_keys ?? 0)}
          testId="customer-manager-pool-metric-active-keys"
        />
        <KpiCard
          label={t('customer_manager_pool.metric.requests')}
          value={fmtInt(pool?.requests_30d ?? 0)}
          testId="customer-manager-pool-metric-requests"
        />
        <KpiCard
          label={t('customer_manager_pool.metric.risks')}
          value={fmtInt(pool?.customers_with_risk ?? 0)}
          testId="customer-manager-pool-metric-risks"
        />
      </div>

      <form
        className="flex flex-wrap items-center gap-3 rounded-lg border border-line-soft bg-card p-4 max-md:p-3"
        onSubmit={handleSearch}
        data-testid="customer-manager-pool-search-form"
        noValidate
      >
        <Input
          type="search"
          value={draftEmail}
          onChange={(e) => setDraftEmail(e.currentTarget.value)}
          placeholder={t('customer_manager_pool.search.placeholder')}
          aria-label={t('customer_manager_pool.search.aria_label')}
          data-testid="customer-manager-pool-search-input"
          wrapperClassName="min-w-72 flex-1 max-md:min-w-0 max-md:basis-full"
        />
        <Button type="submit" variant="primary" data-testid="customer-manager-pool-search-submit" className="max-md:flex-1">
          {t('customer_manager_pool.search.submit')}
        </Button>
        {scope.email.length > 0 ? (
          <Button
            variant="ghost"
            onClick={handleClear}
            data-testid="customer-manager-pool-search-clear"
            className="max-md:flex-1"
          >
            {t('customer_manager_pool.search.clear')}
          </Button>
        ) : null}
      </form>

      {status !== null ? (
        <div
          data-testid="customer-manager-pool-claim-status"
          role="status"
          className={[
            'rounded-lg border px-4 py-3 text-body font-medium',
            status.kind === 'success'
              ? 'border-ok-line bg-ok-dim/50 text-ok'
              : 'border-bad-line bg-bad-dim/50 text-bad-bright',
          ].join(' ')}
        >
          {status.message}
        </div>
      ) : null}

      {isAdmin && opsUsers.isError ? (
        <div
          role="alert"
          data-testid="customer-manager-pool-managers-error"
          className="rounded-lg border border-bad-line bg-bad-dim/50 px-4 py-3 text-body text-bad-bright"
        >
          {opsUsers.error instanceof OpsApiError
            ? `${opsUsers.error.code}: ${opsUsers.error.message}`
            : t('customer_manager_pool.assign.managers_error')}
        </div>
      ) : null}

      {query.isError ? (
        <ErrorBanner
          testId="customer-manager-pool-error"
          title={t('customer_manager_pool.error.title')}
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : null}

      {query.isPending ? <LoadingRows /> : null}

      {query.isSuccess && rows.length === 0 ? (
        <EmptyState
          testId="customer-manager-pool-empty"
          title={
            scope.email.length > 0
              ? t('customer_manager_pool.empty.filtered.title')
              : t('customer_manager_pool.empty.title')
          }
          hint={
            scope.email.length > 0
              ? t('customer_manager_pool.empty.filtered.body')
              : t('customer_manager_pool.empty.body')
          }
          className="rounded-lg border border-line-soft bg-card"
        />
      ) : null}

      {isMobile && query.isSuccess && rows.length > 0 ? (
        <PortfolioSortControl
          sort={scope.sort}
          columns={poolSortColumns}
          onSortChange={handleSortChange}
          testIdPrefix="customer-manager-pool"
        />
      ) : null}

      {query.isSuccess && rows.length > 0 ? (
        <>
          {isMobile ? (
            <div data-testid="customer-manager-pool-cards" className="flex flex-col gap-3">
              {rows.map((row) => (
                <PoolCard
                  key={row.org_id}
                  customer={row}
                  isAdmin={isAdmin}
                  canClaim={canClaim}
                  isClaiming={claim.isPending}
                  assignableManagers={assignableManagers}
                  managersLoading={opsUsers.isPending}
                  managersUnavailable={opsUsers.isError}
                  isAssigning={assign.isPending}
                  cnyPerUsd={cnyPerUsd}
                  onClaim={handleClaim}
                  onAssign={handleAssign}
                />
              ))}
            </div>
          ) : (
          <Table testId="customer-manager-pool-table">
            <THead>
              <Tr interactive={false}>
                <SortableTh
                  label={t('customer_manager_pool.column.customer')}
                  sortKey="email"
                  sort={scope.sort}
                  onSort={handleSort}
                  testId="customer-manager-pool-sort-email"
                />
                <SortableTh
                  label={t('customer_manager_pool.column.org')}
                  sortKey="org"
                  sort={scope.sort}
                  onSort={handleSort}
                  testId="customer-manager-pool-sort-org"
                />
                <SortableTh
                  label={t('customer_manager_pool.column.created_at')}
                  sortKey="created_at"
                  sort={scope.sort}
                  onSort={handleSort}
                  testId="customer-manager-pool-sort-created-at"
                />
                <SortableTh
                  num
                  label={t('customer_manager_pool.column.balance')}
                  sortKey="balance"
                  sort={scope.sort}
                  onSort={handleSort}
                  testId="customer-manager-pool-sort-balance"
                />
                <SortableTh
                  label={t('customer_manager_pool.column.usage')}
                  sortKey="requests"
                  sort={scope.sort}
                  onSort={handleSort}
                  testId="customer-manager-pool-sort-requests"
                />
                <SortableTh
                  label={t('customer_manager_pool.column.keys')}
                  sortKey="keys"
                  sort={scope.sort}
                  onSort={handleSort}
                  testId="customer-manager-pool-sort-keys"
                />
                <SortableTh
                  label={t('customer_manager_pool.column.risks')}
                  sortKey="risks"
                  sort={scope.sort}
                  onSort={handleSort}
                  testId="customer-manager-pool-sort-risks"
                />
                <Th className="text-right">{t('customer_manager_pool.column.actions')}</Th>
              </Tr>
            </THead>
            <TBody>
              {rows.map((row) => (
                <PoolRow
                  key={row.org_id}
                  customer={row}
                  isAdmin={isAdmin}
                  canClaim={canClaim}
                  isClaiming={claim.isPending}
                  assignableManagers={assignableManagers}
                  managersLoading={opsUsers.isPending}
                  managersUnavailable={opsUsers.isError}
                  isAssigning={assign.isPending}
                  cnyPerUsd={cnyPerUsd}
                  onClaim={handleClaim}
                  onAssign={handleAssign}
                />
              ))}
            </TBody>
          </Table>
          )}
          <nav
            aria-label={t('customer_manager_pool.pagination.aria_label')}
            data-testid="customer-manager-pool-pagination"
            className="flex items-center justify-between gap-4 text-dense text-paper-low max-md:flex-wrap max-md:gap-3"
          >
            <span>
              {t('customer_manager_pool.pagination.range', {
                start: currentOffset + 1,
                end: Math.min(currentOffset + rows.length, total),
                total,
              })}
            </span>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                disabled={!hasPrev || query.isFetching}
                onClick={() =>
                  setScope((prev) => ({
                    ...prev,
                    offset: Math.max(0, prev.offset - PAGE_LIMIT),
                  }))
                }
                data-testid="customer-manager-pool-pagination-prev"
              >
                {t('common.previous')}
              </Button>
              <Button
                size="sm"
                disabled={!hasNext || query.isFetching}
                onClick={() => setScope((prev) => ({ ...prev, offset: prev.offset + PAGE_LIMIT }))}
                data-testid="customer-manager-pool-pagination-next"
              >
                {t('common.next')}
              </Button>
            </div>
          </nav>
        </>
      ) : null}
    </section>
  );
}

function PoolRow({
  customer,
  isAdmin,
  canClaim,
  isClaiming,
  assignableManagers,
  managersLoading,
  managersUnavailable,
  isAssigning,
  cnyPerUsd,
  onClaim,
  onAssign,
}: {
  readonly customer: CustomerManagerPortfolioCustomer;
  readonly isAdmin: boolean;
  readonly canClaim: boolean;
  readonly isClaiming: boolean;
  readonly assignableManagers: readonly OpsUserRecord[];
  readonly managersLoading: boolean;
  readonly managersUnavailable: boolean;
  readonly isAssigning: boolean;
  readonly cnyPerUsd: number | null;
  readonly onClaim: (customer: CustomerManagerPortfolioCustomer) => Promise<void>;
  readonly onAssign: (
    customer: CustomerManagerPortfolioCustomer,
    customerManagerOpsUserId: string,
  ) => Promise<void>;
}): JSX.Element {
  const t = useT();
  return (
    <Tr data-testid={`customer-manager-pool-row-${customer.org_id}`}>
      <Td>
        <CustomerCell
          orgId={customer.org_id}
          secondary={
            <div className="flex min-w-0 flex-col items-start gap-1">
              <span className="max-w-64 truncate text-body font-medium text-paper">
                {customer.email}
              </span>
              <BrandChip
                brand={customer.signup_brand}
                testId={`customer-manager-pool-row-brand-${customer.org_id}`}
              />
              <span className="font-mono text-micro text-paper-low">{truncate(customer.account_id)}</span>
            </div>
          }
        />
      </Td>
      <Td>
        <div className="flex min-w-0 flex-col gap-1">
          <span className="max-w-56 truncate">{customer.org_name}</span>
          <span className="font-mono text-micro text-paper-low">{truncate(customer.org_id)}</span>
        </div>
      </Td>
      <Td className="whitespace-nowrap">{formatLocalTimestamp(customer.created_at)}</Td>
      <Td num emphasis>
        {formatUsdMicrosOrCentsAsCnyDisplayBalance(
          customer.balance_usd_micros,
          customer.balance_usd_cents,
          cnyPerUsd,
        )}
      </Td>
      <Td>
        <div className="flex flex-col gap-1">
          <span className="font-mono text-paper">{fmtInt(customer.usage.request_count)} req</span>
          <span className="text-label text-paper-low">
            {customer.usage.error_count > 0
              ? t('customer_manager_pool.usage.errors', { count: customer.usage.error_count })
              : t('customer_manager_pool.usage.no_errors')}
          </span>
        </div>
      </Td>
      <Td>
        <div className="flex flex-col gap-1">
          <span className="text-paper">
            {t('customer_manager_pool.keys.active', {
              active: customer.api_keys.active,
              total: customer.api_keys.total,
            })}
          </span>
          <span className="text-label text-paper-low">
            {t('customer_manager_pool.keys.last_used', {
              time: formatLocalTimestamp(customer.api_keys.last_used_at),
            })}
          </span>
        </div>
      </Td>
      <Td>
        <div className="flex max-w-72 flex-wrap gap-1">
          {customer.risk_signals.length === 0 ? (
            <StatusChip variant="ok" label={t('customer_manager_pool.risk.none')} />
          ) : (
            customer.risk_signals.slice(0, 3).map((signal) => (
              <StatusChip key={signal.code} variant={riskVariant(signal)} label={signal.label} />
            ))
          )}
        </div>
      </Td>
      <Td className="text-right">
        {isAdmin ? (
          <PoolAssignControls
            customer={customer}
            assignableManagers={assignableManagers}
            managersLoading={managersLoading}
            managersUnavailable={managersUnavailable}
            isAssigning={isAssigning}
            onAssign={onAssign}
          />
        ) : (
          <Button
            size="sm"
            disabled={!canClaim || isClaiming}
            onClick={() => void onClaim(customer)}
            data-testid={`customer-manager-pool-row-${customer.org_id}-claim`}
          >
            <UserPlus aria-hidden="true" className="h-3.5 w-3.5" />
            {isClaiming
              ? t('customer_manager_pool.claim.claiming')
              : t('customer_manager_pool.claim.action')}
          </Button>
        )}
      </Td>
    </Tr>
  );
}

function PoolCard({
  customer,
  isAdmin,
  canClaim,
  isClaiming,
  assignableManagers,
  managersLoading,
  managersUnavailable,
  isAssigning,
  cnyPerUsd,
  onClaim,
  onAssign,
}: {
  readonly customer: CustomerManagerPortfolioCustomer;
  readonly isAdmin: boolean;
  readonly canClaim: boolean;
  readonly isClaiming: boolean;
  readonly assignableManagers: readonly OpsUserRecord[];
  readonly managersLoading: boolean;
  readonly managersUnavailable: boolean;
  readonly isAssigning: boolean;
  readonly cnyPerUsd: number | null;
  readonly onClaim: (customer: CustomerManagerPortfolioCustomer) => Promise<void>;
  readonly onAssign: (
    customer: CustomerManagerPortfolioCustomer,
    customerManagerOpsUserId: string,
  ) => Promise<void>;
}): JSX.Element {
  const t = useT();
  const facts: CustomerSummaryFact[] = [
    {
      label: t('customer_manager_pool.column.created_at'),
      value: formatLocalTimestamp(customer.created_at),
    },
    {
      label: t('customer_manager_pool.column.balance'),
      value: formatUsdMicrosOrCentsAsCnyDisplayBalance(
        customer.balance_usd_micros,
        customer.balance_usd_cents,
        cnyPerUsd,
      ),
      num: true,
      emphasis: true,
    },
    {
      label: t('customer_manager_pool.column.usage'),
      value: (
        <span className="flex flex-col">
          <span>{fmtInt(customer.usage.request_count)} req</span>
          <span>
            {customer.usage.error_count > 0
              ? t('customer_manager_pool.usage.errors', { count: customer.usage.error_count })
              : t('customer_manager_pool.usage.no_errors')}
          </span>
        </span>
      ),
    },
    {
      label: t('customer_manager_pool.column.keys'),
      value: (
        <span className="flex flex-col">
          <span>
            {t('customer_manager_pool.keys.active', {
              active: customer.api_keys.active,
              total: customer.api_keys.total,
            })}
          </span>
          <span>
            {t('customer_manager_pool.keys.last_used', {
              time: formatLocalTimestamp(customer.api_keys.last_used_at),
            })}
          </span>
        </span>
      ),
    },
  ];
  return (
    <CustomerSummaryCard
      testId={`customer-manager-pool-row-${customer.org_id}`}
      company={{ orgId: customer.org_id }}
      title={customer.email}
      badges={
        <BrandChip
          brand={customer.signup_brand}
          testId={`customer-manager-pool-row-brand-${customer.org_id}`}
        />
      }
      subtitle={customer.org_name}
      idLine={`${truncate(customer.account_id)} · ${truncate(customer.org_id)}`}
      facts={facts}
      extra={
        customer.risk_signals.length === 0 ? (
          <StatusChip variant="ok" label={t('customer_manager_pool.risk.none')} />
        ) : (
          customer.risk_signals.slice(0, 3).map((signal) => (
            <StatusChip key={signal.code} variant={riskVariant(signal)} label={signal.label} />
          ))
        )
      }
      actions={
        isAdmin ? (
          <PoolAssignControls
            customer={customer}
            assignableManagers={assignableManagers}
            managersLoading={managersLoading}
            managersUnavailable={managersUnavailable}
            isAssigning={isAssigning}
            onAssign={onAssign}
            layout="stack"
          />
        ) : (
          <Button
            size="sm"
            disabled={!canClaim || isClaiming}
            onClick={() => void onClaim(customer)}
            data-testid={`customer-manager-pool-row-${customer.org_id}-claim`}
            className="w-full"
          >
            <UserPlus aria-hidden="true" className="h-3.5 w-3.5" />
            {isClaiming
              ? t('customer_manager_pool.claim.claiming')
              : t('customer_manager_pool.claim.action')}
          </Button>
        )
      }
    />
  );
}

function PoolAssignControls({
  customer,
  assignableManagers,
  managersLoading,
  managersUnavailable,
  isAssigning,
  onAssign,
  layout = 'row',
}: {
  readonly customer: CustomerManagerPortfolioCustomer;
  readonly assignableManagers: readonly OpsUserRecord[];
  readonly managersLoading: boolean;
  readonly managersUnavailable: boolean;
  readonly isAssigning: boolean;
  readonly onAssign: (
    customer: CustomerManagerPortfolioCustomer,
    customerManagerOpsUserId: string,
  ) => Promise<void>;
  readonly layout?: 'row' | 'stack';
}): JSX.Element {
  const t = useT();
  const [selectedManagerId, setSelectedManagerId] = useState('');
  const hasManagers = assignableManagers.length > 0;
  const disabled =
    managersLoading ||
    managersUnavailable ||
    !hasManagers ||
    selectedManagerId.length === 0 ||
    isAssigning;
  const options = assignableManagers.map((manager) => ({
    value: manager.id,
    label: opsUserLabel(manager),
  }));
  const placeholder = managersLoading
    ? t('customer_manager_pool.assign.loading_managers')
    : hasManagers
      ? t('customer_manager_pool.assign.select_placeholder')
      : t('customer_manager_pool.assign.no_managers');
  const selectDisabled = managersLoading || managersUnavailable || !hasManagers || isAssigning;

  if (layout === 'stack') {
    return (
      <div className="flex flex-col gap-2">
        <Select
          value={selectedManagerId.length === 0 ? undefined : selectedManagerId}
          onValueChange={setSelectedManagerId}
          options={options}
          placeholder={placeholder}
          disabled={selectDisabled}
          ariaLabel={t('customer_manager_pool.assign.select_placeholder')}
          testId={`customer-manager-pool-row-${customer.org_id}-manager-select`}
          className="w-full"
        />
        <Button
          size="sm"
          disabled={disabled}
          onClick={() => void onAssign(customer, selectedManagerId)}
          data-testid={`customer-manager-pool-row-${customer.org_id}-assign`}
          className="w-full"
        >
          <UserCheck aria-hidden="true" className="h-3.5 w-3.5" />
          {isAssigning
            ? t('customer_manager_pool.assign.assigning')
            : t('customer_manager_pool.assign.action')}
        </Button>
      </div>
    );
  }

  return (
    <div className="ml-auto flex max-w-[360px] flex-wrap items-center justify-end gap-2">
      <Select
        value={selectedManagerId.length === 0 ? undefined : selectedManagerId}
        onValueChange={setSelectedManagerId}
        options={options}
        placeholder={placeholder}
        disabled={selectDisabled}
        ariaLabel={t('customer_manager_pool.assign.select_placeholder')}
        testId={`customer-manager-pool-row-${customer.org_id}-manager-select`}
        className="min-w-44"
      />
      <Button
        size="sm"
        disabled={disabled}
        onClick={() => void onAssign(customer, selectedManagerId)}
        data-testid={`customer-manager-pool-row-${customer.org_id}-assign`}
      >
        <UserCheck aria-hidden="true" className="h-3.5 w-3.5" />
        {isAssigning
          ? t('customer_manager_pool.assign.assigning')
          : t('customer_manager_pool.assign.action')}
      </Button>
    </div>
  );
}

function LoadingRows(): JSX.Element {
  return (
    <div
      data-testid="customer-manager-pool-loading"
      className="flex flex-col gap-2 rounded-lg border border-line-soft bg-card p-4"
    >
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="h-12 animate-pulse rounded-lg bg-raised"
          aria-hidden="true"
        />
      ))}
    </div>
  );
}

function ErrorBanner({
  testId,
  title,
  error,
  onRetry,
}: {
  readonly testId: string;
  readonly title: string;
  readonly error: Error;
  readonly onRetry: () => void;
}): JSX.Element {
  const t = useT();
  return (
    <div
      role="alert"
      data-testid={testId}
      className="flex items-center justify-between gap-4 rounded-lg border border-bad-line bg-bad-dim/50 px-4 py-3 max-md:flex-wrap max-md:gap-3"
    >
      <div className="flex flex-col gap-1">
        <span className="text-body font-medium text-bad-bright">{title}</span>
        <span className="text-dense text-bad">
          {error instanceof OpsApiError
            ? `${error.code}: ${error.message}`
            : t('common.network_error')}
        </span>
      </div>
      <Button variant="danger" size="sm" onClick={onRetry}>
        {t('common.retry')}
      </Button>
    </div>
  );
}
